import { useParams, Link } from "react-router-dom";
import { blogPosts } from "../data/blogPosts";
import { usePageMeta } from "./usePageMeta";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Calendar, Clock, Tag, ChevronRight, Phone } from "lucide-react";

const COMPARION_URL = "https://www.comparioninsurance.com/insurance-agent/california/elk-grove-0488/kenneth-carson";

// Schema.org structured data for blog post (AEO/GEO optimization)
const generateArticleSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": post.title,
  "description": post.metaDescription,
  "image": post.image,
  "author": {
    "@type": "Person",
    "name": "Kenneth Carson",
    "jobTitle": "Insurance Agent",
    "worksFor": {
      "@type": "Organization",
      "name": "Comparion Insurance"
    }
  },
  "publisher": {
    "@type": "Organization",
    "name": "Kenneth Carson Insurance",
    "logo": {
      "@type": "ImageObject",
      "url": "https://public.libertymutual-cdn.com/entities/agents/kenneth-carson.jpg"
    }
  },
  "datePublished": post.publishedDate,
  "dateModified": post.publishedDate,
  "mainEntityOfPage": {
    "@type": "WebPage"
  },
  "keywords": post.keywords.join(", ")
});

// FAQ Schema for AEO optimization
const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  usePageMeta(post ? `${post.title} | Kenneth Carson` : "Article Not Found", post?.metaDescription || "");

  if (!post) {
    return (
      <div className="min-h-screen bg-[#F7F7F5] pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="font-outfit font-bold text-3xl text-[#143628]">Article Not Found</h1>
          <Link to="/blog" className="text-[#C45A41] hover:underline mt-4 inline-block">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F7F5]">
      {/* Schema.org JSON-LD for SEO/AEO/GEO */}
      <script type="application/ld+json">
        {JSON.stringify(generateArticleSchema(post))}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(generateFAQSchema(post.faqs))}
      </script>

      {/* Hero Section */}
      <div className="relative h-[400px] overflow-hidden">
        <img 
          src={post.image} 
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#143628] via-[#143628]/60 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="max-w-4xl mx-auto">
            <Link 
              to="/blog" 
              className="flex w-fit items-center gap-2 text-white/80 hover:text-white mb-4 font-manrope text-sm"
              data-testid="back-to-blog"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
            <span className="inline-block bg-[#C45A41] text-white px-3 py-1 rounded-full text-sm font-manrope mb-4">
              {post.category}
            </span>
            <h1 className="font-outfit font-[850] text-3xl md:text-4xl lg:text-5xl text-white tracking-tight leading-tight" data-testid="blog-post-title">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-white/80 font-manrope text-sm">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.publishedDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-6 py-12">
        <div 
          className="prose prose-lg max-w-none font-manrope text-[#4A6359]
            prose-headings:font-outfit prose-headings:text-[#143628] prose-headings:font-bold
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
            prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
            prose-p:leading-relaxed prose-p:mb-4
            prose-strong:text-[#143628]
            prose-ul:my-4 prose-li:my-1
            prose-a:text-[#C45A41] prose-a:no-underline hover:prose-a:underline prose-a:break-words
            prose-table:text-sm prose-th:text-[#143628] prose-hr:border-[#143628]/10"
          data-testid="blog-content"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ table: ({ node, ...props }) => <div className="overflow-x-auto"><table {...props} /></div> }}>{post.content}</ReactMarkdown>
        </div>

        {/* Keywords for SEO */}
        <div className="mt-8 pt-6 border-t border-[#143628]/10">
          <div className="flex flex-wrap items-center gap-2">
            <Tag className="w-4 h-4 text-[#4A6359]" />
            {post.keywords.map((keyword, index) => (
              <span 
                key={index}
                className="bg-[#EBEAE5] text-[#4A6359] px-3 py-1 rounded-full text-sm font-manrope"
              >
                {keyword}
              </span>
            ))}
          </div>
        </div>

        {/* FAQ Section - Critical for AEO */}
        <section className="mt-12 bg-[#EBEAE5] rounded-3xl p-6 sm:p-8 [overflow-wrap:anywhere]" data-testid="faq-section">
          <h2 className="font-outfit font-bold text-2xl text-[#143628] mb-6">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {post.faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl p-6" itemScope itemType="https://schema.org/Question">
                <h3 className="font-outfit font-bold text-lg text-[#143628] mb-3" itemProp="name">
                  {faq.question}
                </h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <p className="font-manrope text-[#4A6359] leading-relaxed" itemProp="text">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="mt-12 bg-[#143628] rounded-3xl p-8 text-center">
          <h2 className="font-outfit font-bold text-2xl text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="font-manrope text-white/80 mb-6 max-w-xl mx-auto">
            Contact Kenneth Carson today for a free, no-obligation insurance consultation. Serving Elk Grove, Natomas, and Sacramento.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="/#contact"
              className="inline-flex items-center gap-2 bg-[#C45A41] text-white px-8 py-4 rounded-full font-manrope font-semibold hover:bg-[#A84D37] transition-all duration-300"
              data-testid="blog-cta-contact"
            >
              Get Your Free Quote
              <ChevronRight className="w-5 h-5" />
            </a>
            <a 
              href="tel:+19165051000"
              className="inline-flex items-center gap-2 bg-white/10 text-white px-8 py-4 rounded-full font-manrope font-semibold hover:bg-white/20 transition-all duration-300"
              data-testid="blog-cta-call"
            >
              <Phone className="w-5 h-5" />
              (916) 505-1000
            </a>
          </div>
        </section>

        {/* Author Box */}
        <section className="mt-12 bg-white rounded-3xl p-8 flex flex-col md:flex-row items-center gap-6 border border-[#143628]/10">
          <img 
            src="https://public.libertymutual-cdn.com/entities/agents/kenneth-carson.jpg"
            alt="Kenneth Carson - Insurance Agent"
            className="w-24 h-24 rounded-full object-cover border-2 border-[#C45A41]"
          />
          <div>
            <p className="font-manrope text-sm text-[#C45A41] font-semibold mb-1">Written by</p>
            <h3 className="font-outfit font-bold text-xl text-[#143628]">Kenneth Carson</h3>
            <p className="font-manrope text-[#4A6359] text-sm mt-2">
              Kenneth Carson is a licensed insurance agent at Comparion Insurance, serving Elk Grove, Natomas, and the greater Sacramento area. With a background as a Claims Adjuster, he brings unique expertise to help families find the right coverage.
            </p>
            <a 
              href={COMPARION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[#C45A41] font-manrope text-sm mt-2 hover:underline"
            >
              View Full Profile <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </section>
      </article>
    </div>
  );
};

export default BlogPost;
